import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { CsSection } from '@/components/Cs/CsSection'
import { csFieldClass } from '@/components/Cs/csFormStyles'
import { CsHealthBadge } from '@/components/Cs/CsHealthBadge'
import type { CsAccountDetail } from '@/services/csService'
import { cn } from '@/utils/cn'

type Props = {
  account: CsAccountDetail
  onSave: (payload: Record<string, unknown>) => Promise<void>
}

const SECTORS = [
  { key: 'marketing', label: 'Marketing', prefix: 'healthMkt' },
  { key: 'suporte', label: 'Suporte', prefix: 'healthSup' },
  { key: 'projeto', label: 'Projeto', prefix: 'healthProj' },
] as const

const AXES = [
  { key: 'Perf', label: 'Performance', field: 'performance' },
  { key: 'Eng', label: 'Engajamento', field: 'engajamento' },
  { key: 'Com', label: 'Comunicação', field: 'comunicacao' },
] as const

export function CsAccountHealthMatrix({ account, onSave }: Props) {
  const [draft, setDraft] = useState<Record<string, number | ''>>({})
  const [saving, setSaving] = useState(false)
  const dirty = Object.keys(draft).length > 0

  const getValue = (apiKey: string, current: number | null) => {
    if (draft[apiKey] !== undefined) return draft[apiKey]
    return current ?? ''
  }

  const handleSave = async () => {
    const payload: Record<string, unknown> = {}
    for (const sector of SECTORS) {
      for (const axis of AXES) {
        const apiKey = `${sector.prefix}${axis.key}`
        if (draft[apiKey] === undefined) continue
        payload[apiKey] = draft[apiKey] === '' ? null : Number(draft[apiKey])
      }
    }
    if (Object.keys(payload).length === 0) return
    setSaving(true)
    try {
      await onSave(payload)
      setDraft({})
    } finally {
      setSaving(false)
    }
  }

  return (
    <CsSection
      title='Health Score'
      action={
        <div className='flex flex-wrap items-center gap-2'>
          <CsHealthBadge score={account.healthScoreFinal} band={account.healthBand} />
          {dirty && (
            <Button size='sm' variant='primary' disabled={saving} onClick={() => void handleSave()}>
              Salvar
            </Button>
          )}
        </div>
      }
    >
      <p className='mb-4 text-xs text-[var(--text-muted)]'>
        Cada eixo 0–6 · score setorial = média · final = soma dos 3 setores (máx. 18)
      </p>

      <div className='grid gap-3 lg:grid-cols-3'>
        {SECTORS.map((sector) => {
          const sectorData = account.health[sector.key]
          return (
            <div
              key={sector.key}
              className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)]/40 p-3'
            >
              <div className='mb-3 flex items-baseline justify-between gap-2 border-b border-[var(--glass-border)] pb-2'>
                <h4 className='text-sm font-semibold text-[var(--text-primary)]'>{sector.label}</h4>
                <span className='text-sm font-semibold tabular-nums text-accent'>
                  {sectorData.score}
                  <span className='font-normal text-[var(--text-muted)]'>/6</span>
                </span>
              </div>

              <div className='space-y-2'>
                {AXES.map((axis) => {
                  const apiKey = `${sector.prefix}${axis.key}`
                  const current = sectorData[axis.field]
                  const value = getValue(apiKey, current)
                  return (
                    <div key={apiKey} className='grid grid-cols-[1fr_auto] items-center gap-2'>
                      <span className='text-xs text-[var(--text-muted)]'>{axis.label}</span>
                      <select
                        aria-label={`${sector.label} ${axis.label}`}
                        className={cn(csFieldClass, 'h-9 w-[4.5rem] py-1 text-center tabular-nums')}
                        value={String(value)}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            [apiKey]: e.target.value === '' ? '' : Number(e.target.value),
                          }))
                        }
                      >
                        <option value=''>—</option>
                        {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </CsSection>
  )
}
